import { useEffect, useRef, useState } from 'react';

// A celebration is a transient visual reward: badge unlock, level up,
// streak milestone, or perfect day. The player dashboard polls several
// queries (badges, progress, daily-quests); this hook diffs the data
// between renders and queues celebrations when something good happens.
//
// `prefers-reduced-motion` users still get the card (it's informational),
// but the confetti burst is suppressed (handled in the overlay).

export type CelebrationKind = 'badge' | 'level' | 'streak' | 'perfect_day';

export interface Celebration {
  id: string;
  kind: CelebrationKind;
  title: string;
  detail: string;
  icon: string;
}

interface WatchState {
  earnedBadgeIds: Set<string>;
  totalLevel: number;
  streak: number;
  perfectDay: boolean;
}

export function useCelebrations(opts: {
  badges: { id: string; earned: boolean; name: string; icon: string }[] | undefined;
  totalLevel: number | undefined;
  streak: number | undefined;
  allDailyCompleted: boolean | undefined;
  badgeMeta?: Record<string, { rarity: string }>;
}): Celebration[] {
  const { badges, totalLevel, streak, allDailyCompleted } = opts;
  const [queue, setQueue] = useState<Celebration[]>([]);
  const prev = useRef<WatchState | null>(null);

  useEffect(() => {
    if (!badges || totalLevel === undefined || streak === undefined || allDailyCompleted === undefined) {
      return; // wait until all data is loaded
    }
    const earnedIds = new Set(badges.filter((b) => b.earned).map((b) => b.id));
    const state: WatchState = {
      earnedBadgeIds: earnedIds,
      totalLevel,
      streak,
      perfectDay: allDailyCompleted,
    };
    if (!prev.current) {
      prev.current = state;
      return; // first load — don't celebrate the initial state
    }
    const before = prev.current;
    const newCelebrations: Celebration[] = [];

    // New badges earned since last check.
    for (const id of earnedIds) {
      if (!before.earnedBadgeIds.has(id)) {
        const b = badges.find((x) => x.id === id);
        if (b) {
          newCelebrations.push({
            id: `cel-${id}-${Date.now()}`,
            kind: 'badge',
            title: 'Badge Unlocked!',
            detail: b.name,
            icon: b.icon,
          });
        }
      }
    }

    // Level up.
    if (totalLevel > before.totalLevel) {
      newCelebrations.push({
        id: `cel-level-${Date.now()}`,
        kind: 'level',
        title: 'Level Up!',
        detail: `You reached level ${totalLevel}`,
        icon: '⭐',
      });
    }

    // Streak milestones (7, 30, and every 14 for freeze grants — keep it
    // simple: celebrate 7 and 30 specifically, plus any multiple of 14).
    if (streak > before.streak) {
      if (streak === 7) {
        newCelebrations.push({ id: `cel-streak7-${Date.now()}`, kind: 'streak', title: 'On Fire!', detail: '7-day streak 🔥', icon: '🔥' });
      } else if (streak === 30) {
        newCelebrations.push({ id: `cel-streak30-${Date.now()}`, kind: 'streak', title: 'Unstoppable!', detail: '30-day streak 🔥', icon: '💪' });
      }
    }

    // Perfect day (all 3 daily quests complete).
    if (allDailyCompleted && !before.perfectDay) {
      newCelebrations.push({
        id: `cel-perfect-${Date.now()}`,
        kind: 'perfect_day',
        title: 'Perfect Day!',
        detail: 'All 3 daily quests complete (+10 XP)',
        icon: '🎯',
      });
    }

    if (newCelebrations.length > 0) {
      setQueue((q) => [...q, ...newCelebrations]);
    }
    prev.current = state;
  }, [badges, totalLevel, streak, allDailyCompleted]);

  // Remove a celebration from the queue (called by the overlay on dismiss).
  return queue;
}

export function dismissCelebration(queue: Celebration[], id: string, setQueue: (q: Celebration[]) => void): void {
  setQueue(queue.filter((c) => c.id !== id));
}